type UniqueName = string /* Add this if you render more than one instance of Rating on page */

interface BaseProps extends React.SVGProps<SVGSVGElement> {
  size?: number
  uniqueName?: UniqueName
}

interface StarProps extends BaseProps {
  half?: boolean
  fill: string
}

export const Star = ({
  fill,
  stroke,
  half = false,
  size = 32,
  uniqueName,
  ...props
}: StarProps) => {
  const clipPathHalfId = `half-${uniqueName || 'star'}`
  const clipPathEmptyHalfId = `emptyHalf-${uniqueName || 'star'}`
  return (
    <svg
      viewBox='0 0 40 36'
      xmlns='http://www.w3.org/2000/svg'
      height={size}
      width={size}
      {...props}>
      {half ? (
        <>
          <path
            fill={fill}
            stroke={stroke}
            d='M12.8954 11.5557L18.1325 1.52654C18.5688 0.691023 19.8292 0.691023 20.2655 1.52654L25.5025 11.5557L37.2101 13.1638C38.1857 13.2978 38.5752 14.4311 37.8693 15.0815L29.3969 22.8874L31.3964 33.9117C31.563 34.8302 30.5432 35.5305 29.6707 35.0968L19.199 29.8922L8.72728 35.0968C7.85477 35.5305 6.83497 34.8302 7.00155 33.9117L9.00103 22.8874L0.52872 15.0815C-0.177225 14.4311 0.21228 13.2978 1.18783 13.1638L12.8954 11.5557Z'
            vectorEffect='non-scaling-stroke'
            clipPath={`url(#${clipPathHalfId})`}
          />
          <clipPath id={clipPathHalfId}>
            <rect x='0' y='0' width='20' height='40' />
          </clipPath>
          <path
            fill='#f0f1f2'
            stroke='#d2d4d9'
            d='M12.8954 11.5557L18.1325 1.52654C18.5688 0.691023 19.8292 0.691023 20.2655 1.52654L25.5025 11.5557L37.2101 13.1638C38.1857 13.2978 38.5752 14.4311 37.8693 15.0815L29.3969 22.8874L31.3964 33.9117C31.563 34.8302 30.5432 35.5305 29.6707 35.0968L19.199 29.8922L8.72728 35.0968C7.85477 35.5305 6.83497 34.8302 7.00155 33.9117L9.00103 22.8874L0.52872 15.0815C-0.177225 14.4311 0.21228 13.2978 1.18783 13.1638L12.8954 11.5557Z'
            vectorEffect='non-scaling-stroke'
            clipPath={`url(#${clipPathEmptyHalfId})`}
          />
          <clipPath id={clipPathEmptyHalfId}>
            <rect x='20' y='0' width='20' height='40' />
          </clipPath>
        </>
      ) : (
        <path
          fill={fill}
          stroke={stroke === '#f0f1f2' ? '#d2d4d9' : stroke}
          d='M12.8954 11.5557L18.1325 1.52654C18.5688 0.691023 19.8292 0.691023 20.2655 1.52654L25.5025 11.5557L37.2101 13.1638C38.1857 13.2978 38.5752 14.4311 37.8693 15.0815L29.3969 22.8874L31.3964 33.9117C31.563 34.8302 30.5432 35.5305 29.6707 35.0968L19.199 29.8922L8.72728 35.0968C7.85477 35.5305 6.83497 34.8302 7.00155 33.9117L9.00103 22.8874L0.52872 15.0815C-0.177225 14.4311 0.21228 13.2978 1.18783 13.1638L12.8954 11.5557Z'
          vectorEffect='non-scaling-stroke'
        />
      )}
    </svg>
  )
}

export const fillColor = () => {
  const getColor = (variable: string, fallback: string) =>
    typeof window !== 'undefined'
      ? getComputedStyle(document.documentElement).getPropertyValue(variable)
      : fallback

  return {
    success200: getColor('--colour-success-400', '#1f9575'),
    success100: getColor('--colour-success-100', '#2bd0a4')
  }
}

interface FullStarsProps extends BaseProps {
  count: number
  fill?: string
  stroke?: string
}

const FullStars = ({ count, ...rest }: FullStarsProps) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <Star
        key={i}
        fill={fillColor().success100}
        stroke={fillColor().success200}
        {...rest}
      />
    ))}
  </>
)

const HalfStar = (props: BaseProps) => {
  return (
    <Star
      half
      fill={fillColor().success100}
      stroke={fillColor().success200}
      {...props}
    />
  )
}

interface EmptyStarsProps extends BaseProps {
  count: number
}
const EmptyStars = ({ count, size }: EmptyStarsProps) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <Star key={i + 5} fill={'#f0f1f2'} stroke={'#d2d4d9'} size={size} />
    ))}
  </>
)

interface RatingProps extends BaseProps {
  value: number
}

export const Rating = ({ value, ...rest }: RatingProps) => {
  const fullStars = Math.floor(value)
  const halfStar = value - fullStars >= 0.5

  return (
    <>
      <FullStars count={fullStars} {...rest} />
      {halfStar && <HalfStar {...rest} />}
      <EmptyStars count={5 - fullStars - (halfStar ? 1 : 0)} {...rest} />
    </>
  )
}

export const RatingCompact = ({ ...rest }: RatingProps) => {
  const fillAndStroke =
    rest.value === 0
      ? { fill: '#f0f1f2', stroke: '#d2d4d9' }
      : { fill: fillColor().success100 }

  return <Star {...fillAndStroke} {...rest} />
}
